import React, { useEffect } from 'react';
import { TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/solid';
import { useNoteChat } from '../../hooks/useNoteChat';

const SUGGESTIONS = [
  "Draft a referral letter to the patient's primary care physician based on this session.",
  'Draft a workplace or school accommodation note for the patient focused on functional limitations.',
  'Summarize this note into concise bullet points.',
  'List appropriate follow-up questions to explore in the next session.',
  'Create a progress summary highlighting changes since the initial session.',
  'List potential patient education points relevant to this case.',
];

interface Props {
  noteId: string;
}

const FloatingNoteChat = ({ noteId }: Props) => {
  const [open, setOpen] = React.useState(false);
  const [showAllSuggestions, setShowAllSuggestions] = React.useState(false);
  const { messages, input, setInput, send, loading, clearChat, scrollRef } = useNoteChat(noteId);

  const visibleSuggestions = showAllSuggestions ? SUGGESTIONS : SUGGESTIONS.slice(0, 4);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const onSuggestion = (text: string) => {
    if (loading) return;
    setInput(prev => {
      if (!prev) return text;
      if (prev.includes(text)) return prev;
      return (prev.trimEnd() + '\n' + text).slice(0, 1500);
    });
  };

  return (
    <>
      <button
        onClick={() => setOpen(o => !o)}
        aria-label={open ? 'Close note chat' : 'Open note chat'}
        aria-expanded={open}
        className='fixed bottom-5 right-5 z-50 h-14 w-14 rounded-full shadow-lg bg-gradient-to-tr from-brand-navy to-brand-accent text-white flex items-center justify-center hover:scale-105 transition-transform focus:outline-none focus-visible:ring-4 focus-visible:ring-brand-accent/40'
      >
        <ChatBubbleLeftRightIcon className='h-7 w-7' />
      </button>

      {open && (
        <div className='fixed inset-0 z-50 pointer-events-none'>
          <div
            className='absolute inset-0 bg-black/20 pointer-events-auto'
            onClick={() => setOpen(false)}
          />
          <div
            role='dialog'
            aria-modal='true'
            aria-labelledby='note-chat-title'
            className='pointer-events-auto absolute bottom-24 right-5 w-[92%] sm:w-[420px] h-[620px] flex flex-col rounded-2xl border border-brand-navy/15 bg-white shadow-2xl overflow-hidden'
          >
            <div className='flex items-center gap-2 px-4 py-3 border-b border-brand-navy/15 bg-gradient-to-r from-white/90 to-brand-accent/5'>
              <span id='note-chat-title' className='font-semibold text-sm text-brand-navy'>AskAI</span>
              <div className='ml-auto flex items-center gap-2'>
                <button
                  onClick={clearChat}
                  title='Clear chat'
                  aria-label='Clear chat'
                  className='p-1.5 rounded text-brand-navy/60 hover:text-brand-navy/80 hover:bg-brand-navy/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent/40'
                >
                  <TrashIcon className='h-4 w-4' />
                </button>
                <button
                  onClick={() => setOpen(false)}
                  title='Close'
                  aria-label='Close chat'
                  className='p-1.5 rounded text-brand-navy/60 hover:text-brand-navy/80 hover:bg-brand-navy/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent/40'
                >
                  <XMarkIcon className='h-5 w-5' />
                </button>
              </div>
            </div>

            <div ref={scrollRef} className='flex-1 overflow-y-auto px-4 py-3 space-y-3 text-[13px] leading-relaxed'>
              {messages.length === 0 && !loading && (
                <div className='text-xs text-gray-400 text-center mt-8'>
                  Ask contextual questions about this SOAP note.
                </div>
              )}
              {messages.map((m, i) => {
                const isUser = m.role === 'user';
                const isPlaceholder = !m.content && !isUser && loading && i === messages.length - 1;
                return (
                  <div key={i} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-2xl px-3 py-2 shadow-sm border text-[12.5px] whitespace-pre-wrap break-words ${isUser ? 'bg-brand-navy text-white border-brand-navy' : 'bg-white border-brand-navy/15 text-brand-navy/90'}`}>
                      {isPlaceholder ? (
                        <span className='inline-flex gap-1'>
                          <span className='w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.2s]' />
                          <span className='w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.05s]' />
                          <span className='w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce' />
                        </span>
                      ) : (
                        m.content
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className='border-t border-gray-200 p-3 bg-white/95'>
              <div className='mb-2 flex gap-2 overflow-x-auto'>
                {visibleSuggestions.map(s => (
                  <button
                    key={s}
                    type='button'
                    onClick={() => onSuggestion(s)}
                    disabled={loading}
                    title={s}
                    className='flex-shrink-0 text-[10px] px-2 py-1 rounded-full border border-brand-navy/20 bg-white hover:bg-brand-accent/5 text-brand-navy/70 disabled:opacity-40 max-w-[220px] truncate focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent/40'
                  >
                    {s}
                  </button>
                ))}
                {SUGGESTIONS.length > 4 && (
                  <button
                    onClick={() => setShowAllSuggestions(s => !s)}
                    className='flex-shrink-0 text-[10px] px-2 py-1 rounded-full border border-brand-navy/20 bg-brand-accent/10 hover:bg-brand-accent/20 text-brand-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent/40'
                  >
                    {showAllSuggestions ? 'Less' : 'More'}
                  </button>
                )}
              </div>

              <div className='flex gap-2 items-end'>
                <textarea
                  value={input}
                  disabled={loading}
                  onChange={e => setInput(e.target.value.slice(0, 1500))}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  rows={2}
                  placeholder='Ask about the note...'
                  className='flex-1 text-xs rounded-lg border border-brand-navy/20 focus:outline-none focus:ring-2 focus:ring-brand-accent/60 p-2 resize-none bg-white/80 placeholder:text-brand-navy/40'
                />
                <button
                  onClick={send}
                  disabled={loading || !input.trim()}
                  className='h-9 px-4 rounded-lg text-xs font-medium bg-brand-navy text-white disabled:opacity-40 disabled:cursor-not-allowed shadow hover:bg-brand-navy/90'
                >
                  {loading ? '...' : 'Send'}
                </button>
              </div>
              <div className='mt-1 flex justify-between text-[10px] text-gray-400'>
                <span>{1500 - input.length} chars left</span>
                {loading && <span>Thinking…</span>}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingNoteChat;
